import type { ActivityCell, ActivityItem } from '@lib/api-types/views';
import { useMemo, useState, type ReactNode } from 'react';

import { ConnectReposEmpty } from '../components/EmptyState.tsx';
import { ErrorView, Skeleton } from '../components/Skeleton.tsx';
import { useTooltipHandlers } from '../components/Tooltip.tsx';
import type { BusinessFunction, DerivedContext } from '../data-views.ts';
import { useActivityView } from '../hooks/useViews.ts';

const SUBTITLE =
  'Recent commits, each scored as a blast-radius heatmap across the platform.';

export function ActivityView() {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useActivityView();

  if (error) {
    return <ErrorView title='Activity' error={error} />;
  }
  if (isLoading || !data) {
    return <Skeleton title='Activity' subtitle={SUBTITLE} rows={5} />;
  }

  const firstPage = data.pages[0]!;
  const items = data.pages.flatMap((p) => p.items);

  return (
    <section>
      <h1 className='snap-h1'>Activity</h1>
      <p className='snap-subtitle'>{SUBTITLE}</p>

      <ActivityList
        items={items}
        derivedContexts={firstPage.derivedContexts}
        functions={firstPage.business_functions}
        hasMore={Boolean(hasNextPage)}
        isLoadingMore={isFetchingNextPage}
        onLoadMore={() => void fetchNextPage()}
      />
    </section>
  );
}

/**
 * Standalone activity feed. Used by both the dedicated Activity
 * view and the Timeline view's activity control panel. When `hasMore`
 * is true and `onLoadMore` is supplied, a "See more" footer button
 * appears below the list.
 *
 * `emptyState` overrides what renders when there are no items. The
 * standalone Activity page leaves it unset and gets the repo-connect
 * prompt; the Timeline sidebar passes a plain copy box so repo links
 * live only in the main content panel, not the activity bar.
 */
export function ActivityList({
  items,
  derivedContexts,
  functions,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  emptyState,
}: {
  items: ActivityItem[];
  derivedContexts: DerivedContext[];
  functions: BusinessFunction[];
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  emptyState?: ReactNode;
}) {
  if (items.length === 0) {
    return <>{emptyState ?? <ConnectReposEmpty title='No activity yet' />}</>;
  }
  return (
    <div className='snap-activity-list'>
      {items.map((item) => (
        <ActivityCard
          key={item.sha}
          item={item}
          derivedContexts={derivedContexts}
          functions={functions}
        />
      ))}
      {hasMore && onLoadMore ? (
        <button
          type='button'
          className='snap-activity-list__more'
          onClick={onLoadMore}
          disabled={isLoadingMore}
          aria-label='Load more commits'
        >
          {isLoadingMore ? 'Loading…' : 'See more'}
        </button>
      ) : null}
    </div>
  );
}

interface ActivityCardProps {
  item: ActivityItem;
  derivedContexts: DerivedContext[];
  functions: BusinessFunction[];
}

function ActivityCard({ item, derivedContexts, functions }: ActivityCardProps) {
  const [expanded, setExpanded] = useState(false);
  const author = item.author_name ?? item.author_login ?? 'unknown';
  const date = new Date(item.authored_at * 1000).toLocaleDateString();

  // When the API has a GitHub URL for the commit (redesign composer
  // path), render the subject as a link that opens the commit in a new
  // tab. Fall back to the expand/collapse button for the legacy
  // composer path where `commit_url` is null.
  const titleNode = item.commit_url ? (
    <a
      href={item.commit_url}
      target='_blank'
      rel='noopener noreferrer'
      className='snap-activity-card__title'
      title={item.subject}
    >
      {item.subject}
    </a>
  ) : (
    <button
      type='button'
      className={`snap-activity-card__title${expanded ? ' is-expanded' : ''}`}
      onClick={() => setExpanded((v) => !v)}
      aria-expanded={expanded}
      title={expanded ? 'Collapse' : 'Expand'}
    >
      {item.subject}
    </button>
  );

  return (
    <article className='snap-activity-card'>
      <MiniHeatmap
        cells={item.cells}
        title={item.subject}
        derivedContexts={derivedContexts}
        functions={functions}
      />
      <div>
        {titleNode}
        <p className='snap-activity-card__meta'>
          <code>{item.sha_short}</code> · {author} · {date}
        </p>
        <p className='snap-activity-card__stats'>
          <span className='add'>+{item.additions}</span>{' '}
          <span className='del'>−{item.deletions}</span> across{' '}
          {item.cells.filter((c) => c.value > 0).length} cells
        </p>
      </div>
    </article>
  );
}

interface MiniHeatmapProps {
  cells: ActivityCell[];
  title: string;
  derivedContexts: DerivedContext[];
  functions: BusinessFunction[];
}

function MiniHeatmap({ cells, title, derivedContexts, functions }: MiniHeatmapProps) {
  const indexed = useMemo(() => {
    const map = new Map<string, ActivityCell>();
    for (const c of cells) map.set(`${c.derivedContext}|${c.function}`, c);
    return map;
  }, [cells]);

  const style = {
    gridTemplateColumns: `repeat(${functions.length}, 7px)`,
    gridTemplateRows: `repeat(${derivedContexts.length}, 7px)`,
  };

  return (
    <div className='snap-heatmap' style={style}>
      {derivedContexts.map((comp) =>
        functions.map((fn) => {
          const cell = indexed.get(`${comp.id}|${fn.id}`);
          const intensity = cell?.intensity ?? 0;
          const inMap = comp.functions.includes(fn.id);
          // Three states: out-of-map (gray), in-map quiet (light blue),
          // in-map active (teal — lighter at lower intensity, full at >0.5).
          const classBucket =
            intensity > 0
              ? intensity > 0.5
                ? 'active-dark'
                : 'active-light'
              : inMap
                ? 'in-map'
                : '';
          return (
            <HeatCell
              key={`${comp.id}|${fn.id}`}
              comp={comp.display}
              fn={fn.display}
              intensity={intensity}
              value={cell?.value ?? 0}
              classBucket={classBucket}
              title={title}
            />
          );
        }),
      )}
    </div>
  );
}

function HeatCell({
  comp,
  fn,
  intensity,
  value,
  classBucket,
  title,
}: {
  comp: string;
  fn: string;
  intensity: number;
  value: number;
  classBucket: string;
  title: string;
}) {
  const handlers = useTooltipHandlers(
    value === 0
      ? null
      : {
          title: `${comp} · ${fn}`,
          body: `${value} lines · intensity ${Math.round(intensity * 100)}%`,
          sub: title,
        },
  );
  return (
    <div className={`snap-heatmap__cell ${classBucket}`.trim()} {...handlers} />
  );
}
